import { useCallback, useEffect, useRef, useState } from "react";
import { Pause, Play, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TopBar, btnGhost, btnPrimary, inputCls } from "@/components/TopBar";
import { PlaybackMap } from "@/components/PlaybackMap";

const today = () => new Date().toISOString().slice(0, 10);
const SPEEDS = [1, 2, 4, 8];

const fmtTime = (iso) => {
  if (!iso) return "--:--:--";
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(iso);
  return new Date(hasTz ? iso : `${iso}Z`).toLocaleTimeString([], { hour12: false });
};

export default function Playback() {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [date, setDate] = useState(today());
  const [pings, setPings] = useState([]);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(2);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    api
      .get("/vehicles")
      .then(({ data }) => {
        setVehicles(data);
        setVehicleId((prev) => prev || data[0]?.id || "");
      })
      .catch(() => toast.error("Could not load trucks"));
  }, []);

  const loadTrack = useCallback(async () => {
    if (!vehicleId) return;
    setLoading(true);
    setPlaying(false);
    try {
      const { data } = await api.get(`/vehicles/${vehicleId}/history`, {
        params: { date, order: "asc", limit: 5000 },
      });
      setPings(data);
      setIndex(0);
      if (data.length === 0) toast.message("No recorded pings for that day");
    } catch {
      toast.error("Could not load the track");
    } finally {
      setLoading(false);
    }
  }, [vehicleId, date]);

  useEffect(() => {
    if (vehicleId) loadTrack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId]);

  useEffect(() => {
    clearInterval(timer.current);
    if (!playing || pings.length === 0) return;
    timer.current = setInterval(() => {
      setIndex((i) => {
        if (i >= pings.length - 1) {
          setPlaying(false);
          return i;
        }
        return i + 1;
      });
    }, 800 / rate);
    return () => clearInterval(timer.current);
  }, [playing, rate, pings.length]);

  const ping = pings[index];

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-[#0A0A0A]" data-testid="playback-page">
      <TopBar />
      <div className="flex flex-wrap items-end gap-4 border-b border-white/10 px-4 py-4 sm:px-8">
        <div>
          <p className="overline">reporting</p>
          <h1 className="font-head text-2xl font-black tracking-tight">TRIP PLAYBACK</h1>
        </div>
        <div className="ml-auto flex flex-wrap items-end gap-3">
          <div>
            <label className="overline mb-1 block">truck</label>
            <select
              data-testid="playback-vehicle-select"
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              className={`${inputCls} w-40`}
            >
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.plate_number}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="overline mb-1 block">date</label>
            <input
              type="date"
              data-testid="playback-date-input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={`${inputCls} w-40`}
            />
          </div>
          <button data-testid="playback-load-btn" onClick={loadTrack} className={btnPrimary}>
            {loading ? "loading" : "load track"}
          </button>
        </div>
      </div>

      <div className="relative min-h-0 flex-1">
        <PlaybackMap pings={pings} index={index} />
        {pings.length === 0 && (
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs tracking-[0.25em] text-[#52525B]"
            data-testid="playback-empty"
          >
            NO TRACK FOR THIS TRUCK ON THIS DATE
          </div>
        )}
      </div>

      <div className="border-t border-white/10 px-4 py-4 sm:px-8" data-testid="playback-controls">
        <div className="flex flex-wrap items-center gap-4">
          <button
            data-testid="playback-play-btn"
            onClick={() => setPlaying((p) => !p)}
            disabled={pings.length === 0}
            className="flex items-center gap-2 border border-[#00FF66] bg-[#00FF66] px-4 py-2 text-[10px] font-bold tracking-[0.2em] text-black transition-colors duration-150 hover:bg-transparent hover:text-[#00FF66] disabled:opacity-40"
          >
            {playing ? <Pause size={12} /> : <Play size={12} />}
            {playing ? "PAUSE" : "PLAY"}
          </button>
          <button
            data-testid="playback-reset-btn"
            onClick={() => {
              setPlaying(false);
              setIndex(0);
            }}
            className={btnGhost}
          >
            <RotateCcw size={12} /> RESET
          </button>
          <div className="flex items-center gap-1">
            {SPEEDS.map((s) => (
              <button
                key={s}
                data-testid={`playback-rate-${s}x`}
                onClick={() => setRate(s)}
                className="border px-2 py-1 text-[10px] tracking-[0.15em]"
                style={
                  rate === s
                    ? { borderColor: "#00F0FF", color: "#00F0FF" }
                    : { borderColor: "rgba(255,255,255,0.12)", color: "#A1A1AA" }
                }
              >
                {s}X
              </button>
            ))}
          </div>
          <div className="ml-auto flex gap-6 text-right">
            <div>
              <p className="overline">time</p>
              <p className="font-head text-lg font-black" data-testid="playback-time">
                {fmtTime(ping?.recorded_at)}
              </p>
            </div>
            <div>
              <p className="overline">speed</p>
              <p className="font-head text-lg font-black text-[#00FF66]" data-testid="playback-speed">
                {Number(ping?.speed_kmh || 0).toFixed(0)}
                <span className="ml-1 text-[10px] font-normal text-[#52525B]">KM/H</span>
              </p>
            </div>
            <div>
              <p className="overline">frame</p>
              <p className="font-head text-lg font-black" data-testid="playback-frame">
                {pings.length ? index + 1 : 0}/{pings.length}
              </p>
            </div>
          </div>
        </div>

        <input
          type="range"
          data-testid="playback-slider"
          min="0"
          max={Math.max(0, pings.length - 1)}
          value={index}
          onChange={(e) => {
            setPlaying(false);
            setIndex(Number(e.target.value));
          }}
          className="mt-4 w-full accent-[#00F0FF]"
        />
      </div>
    </div>
  );
}
